import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { Chip, Theme } from '@mui/material';

import {
  CurrentTrainingSessionStatus,
  ModuleMemberTrainingStatus_ModuleMemberFragment,
} from 'src/generated/mloop-graphql';

import ModuleMemberPausedChip from './ModuleMemberPausedChip';
import ModuleMemberPendingChip from './ModuleMemberPendingChip';

interface Fragments {
  moduleMember: ModuleMemberTrainingStatus_ModuleMemberFragment;
}

const ModuleMemberTrainingStatus: FCWithFragments<Fragments> = ({ moduleMember }) => {
  const { t } = useTranslation('hiring-dashboard-interviewers');

  if (
    moduleMember.employee?.isDirectoryDisabled ||
    moduleMember.employee?.isAtsDisabled ||
    !moduleMember.employee?.hasAtsId
  ) {
    return (
      <Chip
        label={t('interviewers.moduleMember.inactive')}
        variant="filled"
        size="medium"
        color="secondary"
        sx={{
          border: (theme: Theme) => `1px solid ${theme.palette.secondary.dark}`,
        }}
      />
    );
  }

  if (moduleMember.stats?.currentTrainingSessionStatus === CurrentTrainingSessionStatus.NeedsApproval) {
    return <ModuleMemberPendingChip moduleMember={moduleMember} />;
  }

  if (moduleMember.employee?.isPaused || moduleMember.isPausedIndefinitely || moduleMember.isPaused) {
    return <ModuleMemberPausedChip moduleMember={moduleMember} />;
  }

  return (
    <Chip
      label={t('interviewers.moduleMember.active')}
      variant="filled"
      size="medium"
      color="secondary"
      sx={{
        border: (theme: Theme) => `1px solid ${theme.palette.secondary.dark}`,
      }}
    />
  );
};

ModuleMemberTrainingStatus.fragments = {
  moduleMember: gql`
    ${ModuleMemberPausedChip.fragments.moduleMember}
    ${ModuleMemberPendingChip.fragments.moduleMember}
    fragment ModuleMemberTrainingStatus_moduleMember on InterviewModuleMember {
      interviewModuleId
      employeeId
      isPausedIndefinitely
      isPaused
      pausedUntil
      stats {
        currentTrainingSessionStatus
      }
      employee {
        id
        isPaused
        timezone
        isAtsDisabled
        isDirectoryDisabled
        hasAtsId
        interviewPauseDates {
          start
          end
        }
      }
      ...ModuleMemberPendingChip_moduleMember
      ...ModuleMemberPausedChip_moduleMember
    }
  `,
};

export default ModuleMemberTrainingStatus;
